import React from 'react';
import {classes, style} from './doppe-viewer-auto-form-field.st.css';
import {flexColumn, reactCreateMemo, useSsrSafeHtmlId} from '@wix/devzai-utils-react';
import type {ReactBasicCompositionProps} from '@wix/devzai-utils-react';
import type {DoppeViewerSettingControlCustom} from '../doppe-viewer-auto-form/doppe-viewer-setting-control-custom';
import {evaluateFunction} from '@wix/devzai-utils-common';
import type {Dictionary} from '@wix/devzai-utils-common';
import {DoppeFormRequiredMarkingMode} from '../../client-server-common';
import {useDoppeViewerLocales} from '../../client/doppe-viewer-locales/doppe-viewer-locales';

/**
 * A form field component for DoppeViewerAutoForm.
 * Renders a label, custom control, and error message.
 */
export const DoppeViewerAutoFormField = reactCreateMemo(function DoppeViewerAutoFormField<VALUE, FORM_DATA extends Dictionary = Dictionary>(
    props: DoppeViewerAutoFormField.Props<VALUE, FORM_DATA>
) {
    const {
        label,
        description,
        control,
        value,
        onValueChange,
        onBlur,
        validationError,
        mandatory = false,
        disabled = false,
        formData,
        className,
        requiredFieldMarkingMode = DoppeFormRequiredMarkingMode.Asterisk,
    } = props;

    const htmlId = useSsrSafeHtmlId();
    const hasError = validationError !== undefined;
    const viewerLocales = useDoppeViewerLocales();

    // The marking is rendered as a small inline suffix on the label. We track *which kind* of
    // suffix it is alongside the text so the styling layer can differentiate:
    //   - Mandatory markings (`*`, `(Required)`) are visually emphasised in the error color —
    //     they signal "this field needs your attention".
    //   - Optional markings (`(Optional)`) are purely informational and use the muted secondary
    //     text color. Using the error color for "(Optional)" reads as a validation error to the
    //     user, which is wrong.
    // The visual gap between the label text and its suffix is applied via CSS
    // (`margin-inline-start` on `.mandatoryIndicator` / `.optionalIndicator`) rather than
    // a hard-coded leading space in the text. A literal " " character is a strong-LTR
    // separator that wrecks the layout in RTL locales — pushing the asterisk to the
    // wrong logical side.
    const labelMarking = evaluateFunction((): {text: string; isMandatory: boolean} | null => {
        switch (requiredFieldMarkingMode) {
            case DoppeFormRequiredMarkingMode.Asterisk: {
                return mandatory ? {text: '*', isMandatory: true} : null;
            }
            case DoppeFormRequiredMarkingMode.RequiredLabel: {
                return mandatory ? {text: viewerLocales['formField.requiredSuffix'], isMandatory: true} : null;
            }
            case DoppeFormRequiredMarkingMode.OptionalLabel: {
                return mandatory ? null : {text: viewerLocales['formField.optionalSuffix'], isMandatory: false};
            }
            case DoppeFormRequiredMarkingMode.None: {
                return null;
            }
        }
    });

    return (
        <div
            className={style(classes.root, {hasError: hasError}, className, flexColumn())}
        >
            {label ? (
                <label
                    htmlFor={htmlId}
                    dir={'auto'}
                    className={style(classes.label)}
                >
                    {label}
                    {labelMarking !== null ? (
                        // No `dir='auto'` here on purpose: the asterisk / "(Required)" suffix
                        // must inherit the label's resolved direction, not auto-detect from
                        // its own content. Auto-detection on a directionally-neutral character
                        // like `*` is browser-inconsistent and can flip the inline margin to
                        // the wrong side in an RTL label.
                        <span
                            className={style(
                                labelMarking.isMandatory ? classes.mandatoryIndicator : classes.optionalIndicator
                            )}
                        >
                            {labelMarking.text}
                        </span>
                    ) : null}
                </label>
            ) : null}

            {description ? (
                <div
                    dir={'auto'}
                    className={style(classes.description)}
                >
                    {description}
                </div>
            ) : null}

            <div className={style(classes.inputContainer)}>
                {control.render({
                    value: value,
                    onValueChange: onValueChange,
                    hasError: hasError,
                    onBlur: onBlur,
                    disabled: disabled,
                    inputId: htmlId,
                    formData: formData,
                })}
            </div>

            {validationError !== undefined ? (
                <div
                    dir={'auto'}
                    className={style(classes.errorMessage)}
                >
                    {validationError}
                </div>
            ) : null}
        </div>
    );
});

export namespace DoppeViewerAutoFormField {

    export interface Props<VALUE, FORM_DATA extends Dictionary = Dictionary> extends ReactBasicCompositionProps {
        /**
         * Label text for the field.
         */
        label?: string;

        /**
         * Optional description text displayed below the label.
         */
        description?: string;

        /**
         * The custom control to render.
         */
        control: DoppeViewerSettingControlCustom<VALUE>;

        /**
         * Current value of the field.
         */
        value: VALUE;

        /**
         * Callback when the value changes.
         */
        onValueChange: (value: VALUE) => void;

        /**
         * Callback when the field loses focus (triggers error reporting).
         */
        onBlur: () => void;

        /**
         * Validation error message to display.
         */
        validationError?: string;

        /**
         * Whether the field is mandatory (shows * indicator).
         */
        mandatory?: boolean;

        /**
         * Whether the field is disabled.
         */
        disabled?: boolean;

        /**
         * The current form data (all field values).
         * Passed to the control's render function.
         */
        formData: FORM_DATA;

        /**
         * How required fields should be marked in the label.
         * Defaults to Asterisk (preserves existing behavior for non-form-action callers).
         */
        requiredFieldMarkingMode?: DoppeFormRequiredMarkingMode;
    }
}
